import { useState, useRef, useCallback, useEffect } from 'react'
import { Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { cn } from '@/utils/cn'
import { formatUserDisplayName } from '@/utils/formatters'
import { getInitials, getAvatarColor } from '@/utils/avatarUtils'
import { useUserSearch } from '@/hooks/useUserSearch'
import { usePlanShares, useSharePlan } from '@/hooks/usePlanShares'
import { toast } from '@/stores/useToastStore'
import type { UserRead } from '@/api/shares'

interface SharePlanModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  planId: string
}

export function SharePlanModal({
  open,
  onOpenChange,
  planId,
}: SharePlanModalProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedUser, setSelectedUser] = useState<UserRead | null>(null)
  const [showDropdown, setShowDropdown] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  // Hooks
  const {
    data: searchData,
    isLoading: isSearching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useUserSearch({ query: searchQuery })
  const { data: sharesData, isLoading: isLoadingShares } = usePlanShares(planId)
  const shareMutation = useSharePlan(planId)

  // Flatten pages to get all users
  const users = searchData?.pages.flatMap(page => page.users) ?? []

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    if (!listRef.current || !hasNextPage || isFetchingNextPage) return

    const { scrollTop, scrollHeight, clientHeight } = listRef.current
    if (scrollHeight - scrollTop - clientHeight < 100) {
      fetchNextPage()
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
    setSelectedUser(null)
    setShowDropdown(true)
  }

  const handleInputFocus = () => {
    if (searchQuery.length >= 2) {
      setShowDropdown(true)
    }
  }

  const handleSelectUser = (user: UserRead) => {
    setSelectedUser(user)
    setSearchQuery(user.email || user.display_name || '')
    setShowDropdown(false)
  }

  const handleSendInvite = () => {
    if (!selectedUser) return

    shareMutation.mutate(
      { user_id: String(selectedUser.id) },
      {
        onSuccess: () => {
          toast.success(
            `Shared plan with ${selectedUser.email || selectedUser.display_name}`
          )
          setSelectedUser(null)
          setSearchQuery('')
        },
        onError: (error: Error) => {
          toast.error(`Failed to share: ${error.message}`)
        },
      }
    )
  }

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setSearchQuery('')
      setSelectedUser(null)
      setShowDropdown(false)
    }
    onOpenChange(isOpen)
  }

  // Show dropdown only when query >= 2 chars
  const shouldShowDropdown = showDropdown && searchQuery.length >= 2

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Share Plan</DialogTitle>
          <DialogDescription>
            Anyone with access can view this project.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Search input row */}
          <div className="flex gap-2">
            <div className="relative flex-1" ref={dropdownRef}>
              <Input
                ref={inputRef}
                placeholder="Search by email or name..."
                value={searchQuery}
                onChange={handleInputChange}
                onFocus={handleInputFocus}
                className="w-full"
              />

              {/* Search Dropdown */}
              {shouldShowDropdown && (
                <div
                  ref={listRef}
                  onScroll={handleScroll}
                  className="absolute top-full left-0 right-0 mt-1 bg-white border border-border rounded-md shadow-lg max-h-[240px] overflow-y-auto z-50"
                >
                  {isSearching && !isFetchingNextPage ? (
                    <div className="px-4 py-6 text-center text-muted-foreground">
                      <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
                      <span className="text-sm">Searching...</span>
                    </div>
                  ) : users.length > 0 ? (
                    <ul className="py-1">
                      {users.map(user => {
                        const colors = getAvatarColor(user.id)
                        return (
                          <li key={user.id}>
                            <button
                              type="button"
                              onClick={() => handleSelectUser(user)}
                              className={cn(
                                'w-full flex items-center gap-3 px-3 py-2',
                                'hover:bg-muted cursor-pointer',
                                'text-left transition-colors duration-150',
                                selectedUser?.id === user.id && 'bg-muted'
                              )}
                            >
                              <Avatar className="h-8 w-8">
                                <AvatarFallback
                                  className={cn(
                                    colors.bg,
                                    colors.text,
                                    'text-xs font-medium'
                                  )}
                                >
                                  {getInitials(user.display_name, user.email)}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">
                                  {user.display_name || user.email}
                                </p>
                                {user.display_name && user.email && (
                                  <p className="text-xs text-muted-foreground truncate">
                                    {user.email}
                                  </p>
                                )}
                              </div>
                            </button>
                          </li>
                        )
                      })}
                      {isFetchingNextPage && (
                        <li className="px-4 py-3 text-center">
                          <Loader2 className="h-4 w-4 animate-spin mx-auto text-muted-foreground" />
                        </li>
                      )}
                    </ul>
                  ) : (
                    <div className="px-4 py-6 text-center text-muted-foreground">
                      <span className="text-sm">
                        No users found for "{searchQuery}"
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
            <Button
              onClick={handleSendInvite}
              disabled={!selectedUser || shareMutation.isPending}
              className="bg-[#CF1F2E] hover:bg-[#b81a27]"
            >
              {shareMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Send Invite'
              )}
            </Button>
          </div>

          {/* People with access */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium">People with access</h3>
            {isLoadingShares ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="space-y-3">
                {/* Owner */}
                {sharesData?.owner && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback
                          className={cn(
                            getAvatarColor(sharesData.owner.id).bg,
                            getAvatarColor(sharesData.owner.id).text,
                            'text-xs font-medium'
                          )}
                        >
                          {getInitials(
                            sharesData.owner.display_name,
                            sharesData.owner.email
                          )}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="text-sm font-medium">
                          {formatUserDisplayName(sharesData.owner)}
                        </p>
                        {sharesData.owner.email && (
                          <p className="text-xs text-muted-foreground">
                            {sharesData.owner.email}
                          </p>
                        )}
                      </div>
                    </div>
                    <span className="text-xs text-muted-foreground">Owner</span>
                  </div>
                )}

                {/* Editors */}
                {sharesData?.items.map(share => {
                  if (!share.user) return null
                  const colors = getAvatarColor(share.user.id)
                  return (
                    <div
                      key={share.id}
                      className="flex items-center justify-between"
                    >
                      <div className="flex items-center gap-2">
                        <Avatar className="h-8 w-8">
                          <AvatarFallback
                            className={cn(
                              colors.bg,
                              colors.text,
                              'text-xs font-medium'
                            )}
                          >
                            {getInitials(
                              share.user.display_name,
                              share.user.email
                            )}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="text-sm font-medium">
                            {formatUserDisplayName(share.user)}
                          </p>
                          {share.user.email && (
                            <p className="text-xs text-muted-foreground">
                              {share.user.email}
                            </p>
                          )}
                        </div>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        Editor
                      </span>
                    </div>
                  )
                })}

                {/* Empty state */}
                {!sharesData?.owner && sharesData?.items.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No one has access yet
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
